"use client";

import { useState, useEffect } from "react";
import { MessageCircle, Phone, Mail, MessageSquare, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SoutenezNotreCause() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [isEnglish, setIsEnglish] = useState(false);
  useEffect(() => {
    const checkLanguage = () => {
      const lang = localStorage.getItem("lacolonne_language");
      setIsEnglish(lang === "en");
    };
    checkLanguage();
    window.addEventListener("storage", checkLanguage);
    window.addEventListener("languagechange", checkLanguage);
    return () => {
      window.removeEventListener("storage", checkLanguage);
      window.removeEventListener("languagechange", checkLanguage);
    };
  }, []);
  const contactMethods = [{
    icon: Mail,
    label: "Email",
    value: "info@lacolonne.ca"
  }, {
    icon: Phone,
    label: isEnglish ? "Phone" : "Téléphone",
    value: isEnglish ? "Mon–Fri 9am–5pm" : "Lun-Ven 9h-17h"
  }, {
    icon: MessageSquare,
    label: isEnglish ? "Office" : "Bureau",
    value: "Edmonton, Alberta"
  }];
  return <section data-section-id="772" id="soutenez-notre-cause" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-[120px] px-4">
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <Sparkles className="absolute top-[12%] left-[8%] size-6 text-secondary/15" />
        <Sparkles className="absolute top-[22%] right-[12%] size-8 text-secondary/10" />
        <Sparkles className="absolute top-[60%] left-[14%] size-5 text-secondary/15" />
        <Sparkles className="absolute bottom-[18%] right-[10%] size-7 text-secondary/12" />
        <Sparkles className="absolute bottom-[30%] left-[40%] size-4 text-secondary/10" />
        <Sparkles className="absolute top-[45%] right-[30%] size-5 text-secondary/10" />
        <Sparkles className="absolute bottom-[10%] left-[28%] size-6 text-secondary/15" />
        <Sparkles className="absolute top-[8%] right-[40%] size-4 text-secondary/10" />
      </div>

      <div ref={ref} className={`relative mx-auto max-w-[720px] text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="size-20 rounded-2xl bg-secondary/20 border border-secondary/30 flex items-center justify-center">
              <MessageCircle className="size-9 text-secondary" strokeWidth={1.5} />
            </div>
            <Sparkles className="absolute -top-1 -right-1 size-5 text-secondary" />
          </div>
        </div>

        <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-secondary font-medium mb-6">
          {isEnglish ? "Building Together" : "Bâtir ensemble"}
        </p>

        <h2 className="font-default font-bold text-foreground text-4xl md:text-5xl lg:text-[56px] leading-[1.1] mb-6">
          {isEnglish ? "Questions about our programs? " : "Des questions sur nos programmes ? "}
          <span className="font-serif italic font-light">
            {isEnglish ? "Let's talk." : "Parlons-en."}
          </span>
        </h2>

        <p className="font-default text-base md:text-lg text-foreground/80 leading-relaxed mb-10 max-w-xl mx-auto">
          {isEnglish ? "Whether you are a senior seeking support or a family looking to get involved, our team is here to facilitate your integration in Edmonton." : "Que vous soyez un aîné cherchant du soutien ou une famille souhaitant s'impliquer, notre équipe est là pour faciliter votre intégration à Edmonton."}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl px-8 py-6 h-auto text-base font-medium shadow-md hover:shadow-lg transition-all w-full sm:w-auto">
            <Link to="/JoinUsPage">
              {isEnglish ? "Join Us" : "Nous Rejoindre"}
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="border-foreground/30 bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground rounded-2xl px-8 py-6 h-auto text-base font-medium transition-all w-full sm:w-auto">
            <Link to="tel:587-501-0890">
              <Phone className="size-4" />
              {isEnglish ? "Call us: (587) 501-0890" : "Appelez-nous : (587) 501-0890"}
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-4 md:gap-6 pt-8 border-t border-foreground/10">
          {contactMethods.map((method, i) => {
          const Icon = method.icon;
          return <div key={i} data-index={i} className="flex items-center gap-2.5 text-sm bg-card/30 border border-foreground/10 rounded-2xl px-5 py-3 shadow-xs">
                <Icon className="size-4 text-secondary shrink-0" />
                <span className="font-default text-foreground/90">
                  <span className="font-medium">{method.label}</span>
                  <span className="text-foreground/50 mx-2">·</span>
                  <span className="text-foreground/75">{method.value}</span>
                </span>
              </div>;
        })}
        </div>
      </div>
    </section>;
}